import {
  AccordionDetails,
  Box,
  Card,
  Container,
  Drawer,
  IconButton,
  Typography,
  styled,
  useMediaQuery,
} from '@mui/material';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import { theme } from '../../../styles/theme';
import { Icon } from '../../icon/icon';
import {
  TALKIATRY_PHONE_NUMBER_DISPLAY,
  TALKIATRY_PHONE_NUMBER_TEL_URL,
} from '../../../helpers/const';
import StyledExternalLink from '../../assessment/shared/privacy-policy-link';
import Link from '../../app/link';
import { WhatsTalkiatry } from '../../app/menu/whats-talkiatry';
import { WhatsPsychiatry } from '../../app/menu/whats-psychiatry';
import { InsuranceFaq } from '../../app/menu/insurance-faq';
import { WhatWeTreat } from '../../app/menu/what-we-treat';
import { WhatWeCantTreat } from '../../app/menu/what-we-cant-treat';

type PropTypes = {
  isMenuOpen: boolean;
  toggleMenu: () => void;
};

const DrawerHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  justifyContent: 'flex-start',
}));

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion
    {...props}
    disableGutters
    TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
  />
))(() => ({}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={
      <Icon
        icon="ChevronDown"
        color="inherit"
        fontSize="small"
        styleType="regular"
      />
    }
    {...props}
  />
))(() => ({
  '&.MuiAccordionSummary-root.Mui-expanded': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    fontWeight: 'bold',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    color: 'inherit',
  },
}));

export const AppMenuDrawer = ({ isMenuOpen, toggleMenu }: PropTypes) => {
  const useTempDrawer = useMediaQuery(theme.breakpoints.down('lg'));
  return (
    <Drawer
      variant={useTempDrawer ? 'temporary' : 'persistent'}
      anchor="right"
      open={isMenuOpen}
      onClose={toggleMenu}
      PaperProps={{
        sx: { width: '78vw', maxWidth: '400px' },
      }}
    >
      <DrawerHeader sx={{ display: { xs: 'none', lg: 'block' } }}>
        <IconButton onClick={toggleMenu}>
          <Icon icon="Xmark" styleType="regular" />
        </IconButton>
      </DrawerHeader>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignContent: 'center',
          height: '100%',
          mt: { xs: 3, sm: 1 },
          p: { xs: 0, sm: 3 },
        }}
      >
        <Accordion>
          <AccordionSummary>What's Talkiatry?</AccordionSummary>
          <AccordionDetails>
            <WhatsTalkiatry />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>What's psychiatry?</AccordionSummary>
          <AccordionDetails>
            <WhatsPsychiatry />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>Insurance FAQ</AccordionSummary>
          <AccordionDetails>
            <InsuranceFaq />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>What we treat</AccordionSummary>
          <AccordionDetails>
            <WhatWeTreat />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>What we can't treat</AccordionSummary>
          <AccordionDetails>
            <WhatWeCantTreat />
          </AccordionDetails>
        </Accordion>

        <Card sx={{ p: 3, my: 3, mx: { xs: 3, sm: 0 }, overflow: 'unset' }}>
          <Box>
            <Box sx={{ display: 'flex' }}>
              <Icon
                icon="CircleQuestion"
                styleType="regular"
                sx={{ mr: 1, mt: 0.25, color: 'rgba(0,0,0,0.6)' }}
              />
              <Typography variant="subtitle1" component="h2">
                Questions?
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ py: 2 }}>
              Give us a call at{' '}
              <StyledExternalLink
                url={TALKIATRY_PHONE_NUMBER_TEL_URL}
                text={TALKIATRY_PHONE_NUMBER_DISPLAY}
              />
            </Typography>
            <Typography variant="body2">
              Email us at{' '}
              <StyledExternalLink
                url="mailto:hello@talkiatry.com"
                text="hello@talkiatry.com"
              />
            </Typography>
          </Box>
        </Card>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',

            alignItems: 'center',
            mt: 'auto',
            mb: 2,
          }}
        >
          <Link
            href="https://talkiatry.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
            color="primary"
            variant="caption"
            underline="none"
          >
            Privacy Policy
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ mx: 2 }}>
            |
          </Typography>
          <Link
            href="https://talkiatry.com/terms-of-use"
            target="_blank"
            rel="noreferrer"
            color="primary"
            variant="caption"
            underline="none"
          >
            Terms of Use
          </Link>
        </Box>
      </Container>
    </Drawer>
  );
};
